import { useCallback, useState } from 'react'
import { api } from '../api/client'
import type { Assessment, Legend, Level, Reason } from '../api/client'
import { useHasPermission } from '../api/session'
import { usePageActions } from '../AppLayout'
import { useItemQuery } from '../hooks/useItemQuery'
import { useListParams } from '../hooks/useListParams'
import { useZielwaehrung } from '../hooks/useZielwaehrung'
import { Card } from '../components/card/Card'
import { Button } from '../components/common/Button'
import { Chip } from '../components/common/Chip'
import { Input } from '../components/common/Controls'
import { FilterBar, FilterEnd, FilterFixed, FilterGrow }
  from '../components/common/FilterBar'
import { MultiSelect } from '../components/common/MultiSelect'
import type { MultiSelectOption } from '../components/common/MultiSelect'
import { EmptyState, ErrorBox } from '../components/common/Feedback'
import { PermissionNotice } from '../components/common/PermissionNotice'
import { Refs, instrumentRef } from '../components/common/Refs'
import { DataTable } from '../components/table/DataTable'
import type { Column } from '../components/table/types'
import { geld, tag } from './priceFormat'
import s from './DailyStretchPage.module.scss'

const PERMISSION = 'report:read'

/**
 * Gesucht wird nicht die billigste Aktie, sondern der beste NORMALE
 * Ruecksetzer in einem Titel, dessen Verfassung weiterhin traegt.
 *
 * Die Ansicht zeigt deshalb nicht nur den Punktwert, sondern die Groessen,
 * aus denen er entsteht: Ein Score von 84 allein ist nicht ueberpruefbar.
 */

type Ton = 'good' | 'info' | 'warn' | 'neutral' | undefined

/** Wie stark ein Status hervorgehoben wird. */
//: Adresse ↔ Auswahl, wie auf den anderen Listen.
function alsListe(wert: string): string[] {
  return wert ? wert.split(',').filter(Boolean) : []
}

function statusTon(status: string): Ton {
  if (status.startsWith('A')) return 'good'
  if (status === 'TRADE') return 'good'
  if (status === 'WATCH') return 'info'
  if (status === 'NO TRADE') return 'warn'
  return undefined
}

/**
 * Dieselbe Farbskala für die Verfassung.
 *
 * Die Farbe trägt hier dieselbe Aussage wie beim Status: Sie sagt vor dem
 * Lesen, ob es eine gute oder eine schlechte Nachricht ist.
 */
function verfassungTon(label: string): Ton {
  if (label === 'sehr stabil') return 'good'
  if (label === 'geeignet') return 'info'
  if (label === 'grenzwertig') return 'neutral'
  if (label === 'ungeeignet') return 'warn'
  return undefined
}

/** Eine Zahl mit Vorzeichen — bei Abweichungen sagt es die halbe Aussage. */
function mitVorzeichen(wert: string | null, stellen = 2): string {
  if (wert === null) return '—'
  const zahl = Number(wert)
  if (!Number.isFinite(zahl)) return wert
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: stellen, maximumFractionDigits: stellen,
    signDisplay: 'exceptZero',
  }).format(zahl)
}

function punkte(wert: number | null): string {
  return wert === null ? '—' : String(wert)
}

/** Eine Dezimalzahl aus dem Server-String, deutsch geschrieben. */
function komma(wert: string | undefined | null, stellen = 2): string {
  if (wert === undefined || wert === null) return '—'
  const zahl = Number(wert)
  return Number.isFinite(zahl)
    ? new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: stellen, maximumFractionDigits: stellen,
      }).format(zahl)
    : wert
}

/**
 * Die Stufen als lesbare Liste — Grundlage jedes Spalten-Tooltips.
 *
 * Sie kommen vom Server, nicht aus einer Konstanten hier: Die Schwellen
 * stehen in der Konfiguration des Reports und ändern sich, sobald jemand die
 * Strategie nachjustiert.
 */
function Stufen({ levels, richtung, ton }: {
  levels: Level[]
  richtung: 'ab' | 'bis'
  /** Färbt die Stufe wie in der Tabelle — die Zuordnung geht dann ohne Lesen. */
  ton: (label: string) => Ton
}) {
  return (
    <ul className={s.stufen}>
      {levels.map((l) => (
        <li key={l.label} className={s.stufe}>
          <Chip tone={ton(l.label)}>{l.label}</Chip>
          <span>
            {/* Ohne Schwelle steht die Bedingung an ihrer Stelle: NO TRADE
                entsteht aus einem Veto, nicht aus einem Punktwert. */}
            {l.bound === null ? '' : `${richtung} ${l.bound} — `}
            {l.meaning}
          </span>
        </li>
      ))}
    </ul>
  )
}

function columns(legend: Legend | undefined): Column<Assessment>[] {
  const g = legend?.thresholds ?? {}
  const wert = (name: string) => komma(g[name])

  return [
  { key: 'name', label: 'Instrument', minWidth: '210px',
    // DIESELBE ZELLE WIE IN MELDUNGEN UND WATCHLIST: Ein Klick zeigt die
    // Eckdaten des Titels, statt sofort wegzunavigieren.
    //
    // OHNE ISIN ODER NAME bleibt es beim schlichten Text: Beide Felder
    // dürfen leer sein, und ein Popover ohne Ziel hätte nichts zu zeigen.
    render: (row) => (row.isin && row.name
      ? <Refs leer="—"
              refs={[instrumentRef(row.isin, row.name, row.instrument_id)]} />
      : <span className={s.name}>{row.name ?? '—'}</span>),
    help: 'Der beobachtete Titel. Ein Klick zeigt seine Eckdaten — Kennung, Kurs, Ampel und die jüngsten Meldungen.' },
  { key: 'status', label: 'Status', minWidth: '120px',
    render: (row) => <Chip tone={statusTon(row.status)}>{row.status}</Chip>,
    help: legend ? (
      <>
        <p>Ein Veto schlägt jeden Punktwert. Sonst gilt der Entry Score:</p>
        <Stufen levels={legend.status} richtung="ab" ton={statusTon} />
      </>
    ) : 'Die Handelsempfehlung in einem Wort.' },
  { key: 'entry_score', label: 'Score', align: 'right', mono: true,
    minWidth: '80px',
    render: (row) => punkte(row.entry_score),
    help: `Halb Verfassung, halb Dip — zwei Fragen, gleich gewichtet: Ist der Titel handelbar? Ist heute ein guter Tag? Leer, sobald ein Veto steht: Daneben noch eine gute Zahl auszuweisen wäre irreführend.` },
  { key: 'condition', label: 'Verfassung', align: 'right', mono: true,
    minWidth: '110px', hideOn: 'mobile',
    render: (row) => punkte(row.condition.score),
    help: legend ? (
      <>
        <p>Trägt der Titel überhaupt? Aus vier Termen: Lage des kurzen
        Preisbands, seiner Unterkante, der Richtung und der Schwankungsbreite.
        Gehandelt wird ab <b>{wert('minimum_condition')}</b>.</p>
        <Stufen levels={legend.condition} richtung="bis"
                ton={verfassungTon} />
      </>
    ) : 'Trägt der Titel überhaupt?' },
  { key: 'dip_score', label: 'Dip', align: 'right', mono: true,
    minWidth: '70px', hideOn: 'mobile',
    render: (row) => punkte(row.dip_score),
    help: (
      <>
        <p>Wie gut der heutige Rücksetzer ist — eine <b>Glocke, kein Hang</b>.
        Zu wenig Abweichung ist uninteressant, zu viel gefährlich.</p>
        <ul className={s.stufen}>
          <li><b>0</b> bei {wert('stretch_begin')} ATR — noch kein Rücksetzer</li>
          <li><b>100</b> bei {wert('stretch_optimum')} ATR — das Optimum</li>
          <li><b>0</b> bei {wert('stretch_end')} ATR — kein normaler Rücksetzer mehr</li>
        </ul>
        <p>Ein stärkerer Rückgang ergibt also NICHT automatisch ein besseres
        Signal.</p>
      </>
    ) },
  { key: 'daily_stretch', label: 'Stretch', align: 'right', mono: true,
    minWidth: '90px', hideOn: 'tablet',
    render: (row) => mitVorzeichen(row.daily_stretch),
    help: `Abstand zum Vortagsschluss, gemessen in ATR — dadurch sind ein ruhiger und ein schwankender Titel vergleichbar. −1,00 heißt: eine ganze normale Tagesspanne tiefer als gestern. Gehandelt wird zwischen ${wert('stretch_trade_max')} und ${wert('stretch_end')}.` },
  { key: 'price', label: 'Kurs', align: 'right', mono: true, minWidth: '100px',
    render: (row) => row.price === null ? '—' : geld(row.price),
    copy: (row) => row.price?.amount ?? '',
    help: 'Der Kurs, auf dem gerechnet wurde — beim laufenden Handelstag der aktuelle, sonst der letzte Schlusskurs.' },
  { key: 'stop', label: 'Stop', align: 'right', mono: true, minWidth: '100px',
    hideOn: 'tablet',
    render: (row) => row.entry.stop === null
      ? <span className={s.fehlt} title={row.entry.stop_missing ?? ''}>—</span>
      : geld(row.entry.stop),
    copy: (row) => row.entry.stop?.amount ?? '',
    help: `Tagestief minus ein Zehntel ATR. Weiter als ${wert('max_stop_atr')} ATR vom Kurs entfernt gilt der Trade als nicht handelbar. Fehlt das Tagestief, wird KEIN Stop gebildet — einer auf dem falschen Tag sähe aus wie eine geprüfte Zahl.` },
  { key: 'target', label: 'Ziel', align: 'right', mono: true, minWidth: '100px',
    hideOn: 'tablet',
    render: (row) => row.entry.target === null
      ? '—' : geld(row.entry.target),
    copy: (row) => row.entry.target?.amount ?? '',
    help: 'Rückkehr zum Vortagsschluss, gedeckelt auf 0,60 ATR. Mehr wäre keine Rückkehr zum Normalpreis mehr, sondern eine Hoffnung.' },
  { key: 'chance', label: 'Potenzial', align: 'right', mono: true,
    minWidth: '100px',
    render: (row) => row.entry.chance === null
      ? '—' : <span className={s.gewinn}>{geld(row.entry.chance)}</span>,
    copy: (row) => row.entry.chance?.amount ?? '',
    help: 'Ziel minus Einstieg, je Stück. Das Verhältnis allein sagt nichts darüber, ob es um vier Euro geht oder um vierzig — und das entscheidet mit, ob sich die Order lohnt.' },
  { key: 'risk', label: 'Risiko', align: 'right', mono: true, minWidth: '100px',
    render: (row) => row.entry.risk === null
      ? '—' : <span className={s.verlust}>{geld(row.entry.risk)}</span>,
    copy: (row) => row.entry.risk?.amount ?? '',
    help: 'Einstieg minus Stop, je Stück. Was der Trade kostet, wenn er nicht aufgeht.' },
  { key: 'risk_reward', label: 'CRV', align: 'right', mono: true,
    minWidth: '80px',
    render: (row) => row.entry.risk_reward === null
      ? '—' : komma(row.entry.risk_reward),
    help: `Chance/Risiko — Potenzial geteilt durch Risiko. Unter ${wert('minimum_risk_reward')} wird nicht gehandelt.` },
  { key: 'reasons', label: 'Grund', minWidth: '280px', wrap: true,
    render: (row) => <Gruende reasons={row.reasons} />,
    help: 'Der wichtigste Grund, in Kurzform — bei einem Veto das, was den Trade verhindert, sonst das, was am meisten für ihn spricht. Ein Klick auf die Zeile zeigt alle Gründe im Klartext.' },
  ]
}

const MARKE: Record<Reason['kind'], string> = {
  veto: '✗', caution: '!', support: '✓',
}

/**
 * Der wichtigste Grund in einer Zeile.
 *
 * Früher stand hier bei einem handelbaren Titel schlicht "handelbar" — das
 * ist kein Grund, sondern eine Wiederholung des Status. Jetzt steht dort,
 * WORAN es liegt, und der Rest hängt am Klick.
 */
function Gruende({ reasons }: { reasons: Reason[] }) {
  if (reasons.length === 0) return <span className={s.frei}>—</span>
  const [erster, ...rest] = reasons
  return (
    <span className={s.vetos} title={reasons.map((r) => r.text).join('\n\n')}>
      <span className={s[erster.kind]}>{MARKE[erster.kind]}</span>
      {erster.short}
      {rest.length > 0 && <span className={s.weitere}>+{rest.length}</span>}
    </span>
  )
}

/** Alle Gründe eines Titels im Klartext. */
function Begruendung({ row }: { row: Assessment }) {
  return (
    <Card title={`${row.name} — warum`} help="Das Fazit und jeder einzelne Grund. Die Kennung in Klammern bleibt stabil, auch wenn der Satz sich ändert — daran hängt eine maschinelle Auswertung.">
      <p className={s.fazit}>{row.summary}</p>
      <ul className={s.liste}>
        {row.reasons.map((r) => (
          <li key={r.code} className={s.grund}>
            <span className={s[r.kind]}>{MARKE[r.kind]}</span>
            <span>
              {r.text}
              <code className={s.code}>{r.code}</code>
            </span>
          </li>
        ))}
      </ul>
    </Card>
  )
}

export function DailyStretchPage() {
  const allowed = useHasPermission(PERMISSION)
  const [gewaehlt, setGewaehlt] = useState<string | null>(null)

  // Die Ladefunktion MUSS stabil sein: Eine Pfeilfunktion an dieser Stelle
  // waere bei jedem Rendern eine neue, und der Hook liefe endlos nach.
  const { waehrung, wahl } = useZielwaehrung(allowed)

  const laden = useCallback(
    (signal: AbortSignal) => api.dailyStretch(
      waehrung ? { currency: [waehrung] } : {}, signal), [waehrung])
  const { item, loading, error, denied } = useItemQuery(laden, allowed)

  // Die Filter stehen in der ADRESSE, wie auf den Listen: teilbar, überlebt
  // das Neuladen, und die Zurück-Taste nimmt den letzten Schritt zurück.
  //
  // `useListParams` OHNE Server-Anbindung — hier verwaltet es nur die
  // Adresse. Das ist kein Missbrauch, sondern die Trennung, die der Hook
  // ohnehin macht: Er kennt keine Abfrage, nur Zustand.
  const list = useListParams({
    filters: { q: '', status: '' },
    sort: 'entry_score',
    descending: true,
  })

  // Oben in der Kopfzeile, nicht in der Filterleiste: Ein Filter entscheidet,
  // WELCHE Zeilen kommen — die Zielwährung nur, wie sie aussehen.
  usePageActions(wahl)

  if (!allowed || denied) {
    return <PermissionNotice permission={PERMISSION}
                             subject="die Auswertungen" />
  }

  const alle = item?.rows ?? []

  // GEFILTERT WIRD IM CLIENT und nicht auf dem Server, anders als bei den
  // Listen. Zwei Gründe, beide aus der Bauart dieses Reports:
  //
  // Der STATUS entsteht erst beim Rechnen. Ein Serverfilter müsste erst alle
  // Titel bewerten und dann wegwerfen — gespart wäre nichts, nur die
  // Übertragung von ein paar Kilobyte.
  //
  // Und es sind ALLE Zeilen da. Der Report kennt keine Seiten; gerechnet wird
  // über die Watchlist, und die hat acht Einträge. Ein Filter über eine
  // vollständig geladene Liste ist eine Auswahl im Speicher, keine Abfrage.
  const gesucht = list.filters.q.trim().toLowerCase()
  const status = alsListe(list.filters.status)
  const zeilen = alle.filter((z) => {
    if (gesucht && !(z.name ?? '').toLowerCase().includes(gesucht)
        && !(z.isin ?? '').toLowerCase().includes(gesucht)) {
      return false
    }
    return status.length === 0 || status.includes(z.status)
  })

  const handelbar = zeilen.filter((z) => z.vetos.length === 0).length

  const ausgewaehlt = zeilen.find((row) => schluessel(row) === gewaehlt)

  // DIE STATUSAUSWAHL KOMMT AUS DER LEGENDE, nicht aus einer Liste im Code.
  //
  // Die Legende steht in `config/reports/daily-stretch.yaml` und kommt mit
  // jeder Antwort mit. Eine verdrahtete Liste liefe auseinander, sobald dort
  // eine Stufe dazukäme — und niemand merkte es, weil ein fehlender Eintrag
  // im Filter nur bedeutet, dass man ihn nicht auswählen kann.
  //
  // In der Reihenfolge der Legende und nicht alphabetisch: Sie ist nach
  // Qualität sortiert, und das ist die Ordnung, in der ein Leser sie erwartet.
  //
  // DIE SCHWELLE STEHT ALS HINWEIS DARUNTER. „A SETUP" allein sagt nicht, wo
  // es anfängt; „ab 80 Punkten" macht die Auswahl ohne einen Blick in die
  // Legende benutzbar. NO TRADE hat keine — es entsteht aus einem VETO und
  // nicht aus einem Punktwert, und dort steht deshalb der Grund.
  const statusOptionen: MultiSelectOption[] =
    (item?.legend.status ?? []).map((l) => ({
      value: l.label,
      label: l.label,
      // KURZ HALTEN: Die Spalte schneidet ab. „Veto — unabhängig vom Score"
      // wurde zu „Veto — unabhängig vom Sc…", und der Rest des Satzes war
      // genau die Aussage.
      hint: l.bound === null ? 'durch Veto' : `ab ${l.bound} Punkten`,
    }))

  const tabelle = (
    <>
      <FilterBar>
        <FilterGrow>
          <Input label="Suche"
                 placeholder="Name oder ISIN"
                 value={list.filters.q} autoFocus
                 onChange={(event) => list.setFilter('q', event.target.value)} />
        </FilterGrow>
        <FilterFixed>
          <MultiSelect label="Status" options={statusOptionen}
                       selected={alsListe(list.filters.status)}
                       onChange={(werte) =>
                         list.setFilter('status', werte.join(','))}
                       placeholder="jeder Status" />
        </FilterFixed>
        <FilterEnd>
          <Button onClick={list.reset} disabled={!list.touched}>
            Zurücksetzen
          </Button>
        </FilterEnd>
      </FilterBar>

    <Card
      title="Daily Stretch"
      flush
      help="Gesucht wird nicht die billigste Aktie, sondern der beste NORMALE Rücksetzer in einem Titel, dessen Verfassung weiterhin trägt. Gerechnet wird über die aktiven Einträge der Watchlist."
      meta={item ? <Kopfzeile rows={zeilen.length} total={alle.length}
                              tradable={handelbar}
                              day={item.trading_date} /> : undefined}
    >
      {error && <ErrorBox>{error}</ErrorBox>}

      {item && item.unknown.length > 0 && (
        <ErrorBox>Nicht im Bestand: {item.unknown.join(', ')}</ErrorBox>
      )}

      <DataTable
        columns={columns(item?.legend)}
        rows={zeilen}
        rowKey={(row) => `${row.isin}-${row.exchange}`}
        loading={loading}
        onRowClick={(row) => setGewaehlt(
          gewaehlt === schluessel(row) ? null : schluessel(row))}
        // Die aufgeklappte Zeile markieren. Die Begründung steht UNTERHALB
        // der Tabelle — ohne Markierung wäre nicht erkennbar, zu welcher
        // Zeile sie gehört.
        rowAttrs={(row): Record<string, string> =>
          schluessel(row) === gewaehlt ? { 'data-active': 'true' } : {}}
        empty={
          // ZWEI VERSCHIEDENE LEEREN. Ein Filter, der nichts trifft, ist
          // etwas anderes als ein Bericht ohne Titel — und der Hinweis "die
          // Watchlist ist leer" wäre dort schlicht falsch.
          list.touched
            ? <EmptyState title="kein Titel passt">
                Kein Eintrag entspricht Suche und Status. Filter
                zurücksetzen oder die Auswahl erweitern.
              </EmptyState>
            : <EmptyState title="Nichts zu bewerten">
                Der Bericht rechnet über die aktiven Einträge der Watchlist.
                Ist dort nichts aktiv, bleibt er leer.
              </EmptyState>
        }
      />
    </Card>
    </>
  )

  return (
    <>
      {tabelle}
      {ausgewaehlt && <Begruendung row={ausgewaehlt} />}
    </>
  )
}

function schluessel(row: Assessment): string {
  return `${row.isin}-${row.exchange}`
}

function Kopfzeile({ rows, total, tradable, day }: {
  rows: number; total: number; tradable: number; day: string | null
}) {
  return (
    <span className={s.fuss}>
      {/*
        „5 von 8 TitelN": der Dativ, den „von" verlangt. Steht die Zahl
        allein, ist es der Nominativ — deshalb zwei Fassungen statt eines
        zusammengesetzten Satzes. Und „von" nur, wenn ein Filter greift:
        sonst stünde dort dauerhaft „8 von 8".
      */}
      {rows < total
        ? `${rows} von ${total} Titeln`
        : `${rows} ${rows === 1 ? 'Titel' : 'Titel'}`}, davon {tradable} handelbar
      {day && <> · Handelstag {tag.format(new Date(day))}</>}
      <span className={s.hinweis}>
        Bei jedem Abruf neu gerechnet — es gibt keine gespeicherten Ergebnisse.
      </span>
    </span>
  )
}
